import React, { useEffect, useMemo, useState } from 'react';
import { arrayOf, bool, number, oneOf, shape, string, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import classNames from 'classnames';
import {
  txIsAccepted,
  txIsCanceled,
  txIsDeclined,
  txIsEnquired,
  txIsRequested,
  txIsPaymentExpired,
  txIsPaymentPending,
  txConfirmedPayment,
  txRequestBookingExtend,
  txIsDelivered,
  txIsOfferExtendBooking,
  txExtendedBooking,
  txCompleteBooking,
  txReview,
  txIsAutoRecreatedPayment,
  txIsCreatedPayment,
  txIsDeclineExtendBooking,
  txIsOffer,
  txIsDisputed,
  txIsPaymentConfirmed,
  txIsRescheduled,
  txIsRescheduleAccepted,
} from '../../util/customTransaction/mainRentalTransaction';
import {
  propTypes,
  DATE_TYPE_DATE,
  DEFAULT_INBOX_SORT_VALUE,
  INBOX_SORT_BY_RETURN_DATE,
  INBOX_SORT_BY_LATEST_CONTACT,
  INBOX_SORT_BY_BOOKING_START,
  INBOX_FILTER_ENQUIRY_ONLY,
  INBOX_FILTER_NOT_YET_COMMENCED,
  INBOX_FILTER_CURRENT_RENTALS,
  INBOX_FILTER_ENDED_RENTALS,
} from '../../util/types';
import { ensureCurrentUser } from '../../util/data';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/UI.duck';
import {
  Avatar,
  BookingTimeInfo,
  NamedLink,
  NotificationBadge,
  Page,
  TabNav,
  LayoutSideNavigation,
  LayoutWrapperMain,
  LayoutWrapperSideNav,
  LayoutWrapperTopbar,
  LayoutWrapperFooter,
  Footer,
  IconSpinner,
  UserDisplayName,
} from '../../components';
import { TopbarContainer, NotFoundPage } from '../../containers';
import config from '../../config';

import {
  loadData,
  filterTransactions,
  sortTransactions,
  resetFilter,
} from './InboxPage.duck';
import css from './InboxPage.css';
import {
  exTxIsCompleteBooking,
  exTxIsConfirmedPayment,
  exTxIsDelivered,
  exTxIsDisputed,
  exTxIsExpirePayment,
  exTxIsExtendedBooking,
  exTxIsOfferExtendBooking,
  exTxIsPendingPayment,
  exTxIsReturnedItem,
} from '../../util/customTransaction/extendRentalTransaction';
import InboxFiltersPanel from './InboxFiltersPanel';
import Pagination from '../../components/Pagination/Pagination';
import { useHistory } from 'react-router-dom';
import { createResourceLocatorString } from '../../util/routes';
import routeConfiguration from '../../routeConfiguration';
import queryString from 'query-string';
import get from 'lodash/get';

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout
const ITEMS_PER_PAGE = 10;
const DEFAULT_QUERY_PARAMS = { sort: DEFAULT_INBOX_SORT_VALUE, page: 1 };

const VALID_SORT_PARAMS = [
  INBOX_SORT_BY_BOOKING_START,
  INBOX_SORT_BY_RETURN_DATE,
  INBOX_SORT_BY_LATEST_CONTACT,
];

const VALID_FILTER_PARAMS = [
  INBOX_FILTER_ENQUIRY_ONLY,
  INBOX_FILTER_NOT_YET_COMMENCED,
  INBOX_FILTER_CURRENT_RENTALS,
  INBOX_FILTER_ENDED_RENTALS,
];

const formatDate = (intl, date) => {
  return {
    short: intl.formatDate(date, {
      month: 'short',
      day: 'numeric',
    }),
    long: `${intl.formatDate(date)} ${intl.formatTime(date)}`,
  };
};

const mockRouteUrl = (pathParams, searchParams) => {
  const urlString = createResourceLocatorString(
    'InboxPage',
    routeConfiguration(),
    pathParams,
    searchParams
  );
  window && window.history.replaceState('', '', urlString);
};

// Translated name of the state of the given transaction
export const txState = (intl, tx, type) => {
  const isOrder = type === 'order';

  if (txReview(tx)) {
    return {
      nameClassName: css.nameNotEmphasized,
      bookingClassName: css.bookingNoActionNeeded,
      lastTransitionedAtClassName: css.lastTransitionedAtNotEmphasized,
      stateClassName: css.stateNoActionNeeded,
      state: intl.formatMessage({
        id: 'InboxPage.stateReview',
      }),
    };
  } else if (txIsEnquired(tx)) {
    return {
      nameClassName: isOrder ? css.nameNotEmphasized : css.nameEmphasized,
      bookingClassName: css.bookingActionNeeded,
      lastTransitionedAtClassName: css.lastTransitionedAtEmphasized,
      stateClassName: css.stateActionNeeded,
      state: intl.formatMessage({
        id: 'InboxPage.stateEnquiry',
      }),
    };
  } else if (txIsOffer(tx)) {
    return {
      nameClassName: css.nameEmphasized,
      bookingClassName: css.bookingActionNeeded,
      lastTransitionedAtClassName: css.lastTransitionedAtEmphasized,
      stateClassName: css.stateActionNeeded,
      state: intl.formatMessage({
        id: 'InboxPage.stateNegotiate',
      }),
    };
  } else if (txIsRequested(tx)) {
    const requested = isOrder
      ? {
          nameClassName: css.nameNotEmphasized,
          bookingClassName: css.bookingNoActionNeeded,
          lastTransitionedAtClassName: css.lastTransitionedAtEmphasized,
          stateClassName: css.stateActionNeeded,
          state: intl.formatMessage({
            id: 'InboxPage.stateRequested',
          }),
        }
      : {
          nameClassName: css.nameEmphasized,
          bookingClassName: css.bookingActionNeeded,
          lastTransitionedAtClassName: css.lastTransitionedAtEmphasized,
          stateClassName: css.stateActionNeeded,
          state: intl.formatMessage({
            id: 'InboxPage.statePending',
          }),
        };

    return requested;
  } else if (txIsRescheduled(tx)) {
    const requested = isOrder
      ? {
          nameClassName: css.nameEmphasized,
          bookingClassName: css.bookingActionNeeded,
          lastTransitionedAtClassName: css.lastTransitionedAtEmphasized,
          stateClassName: css.stateActionNeeded,
          state: intl.formatMessage({
            id: 'InboxPage.stateRescheduled',
          }),
        }
      : {
          nameClassName: css.nameNotEmphasized,
          bookingClassName: css.bookingNoActionNeeded,
          lastTransitionedAtClassName: css.lastTransitionedAtNotEmphasized,
          stateClassName: css.stateSucces,
          state: intl.formatMessage({
            id: 'InboxPage.stateRescheduled',
          }),
        };

    return requested;
  } else if (txIsRescheduleAccepted(tx)) {
    const requested = isOrder
      ? {
          nameClassName: css.nameNotEmphasized,
          bookingClassName: css.bookingNoActionNeeded,
          lastTransitionedAtClassName: css.lastTransitionedAtNotEmphasized,
          stateClassName: css.stateSucces,
          state: intl.formatMessage({
            id: 'InboxPage.stateRescheduleAccepted',
          }),
        }
      : {
          nameClassName: css.nameEmphasized,
          bookingClassName: css.bookingActionNeeded,
          lastTransitionedAtClassName: css.lastTransitionedAtEmphasized,
          stateClassName: css.stateActionNeeded,
          state: intl.formatMessage({
            id: 'InboxPage.stateRescheduleAccepted',
          }),
        };

    return requested;
  } else if (txIsPaymentPending(tx)) {
    return {
      nameClassName: isOrder ? css.nameNotEmphasized : css.nameEmphasized,
      bookingClassName: css.bookingNoActionNeeded,
      lastTransitionedAtClassName: css.lastTransitionedAtNotEmphasized,
      stateClassName: isOrder ? css.stateActionNeeded : css.stateNoActionNeeded,
      state: intl.formatMessage({
        id: 'InboxPage.statePendingPayment',
      }),
    };
  } else if (txIsPaymentExpired(tx)) {
    return {
      nameClassName: css.nameNotEmphasized,
      bookingClassName: css.bookingNoActionNeeded,
      lastTransitionedAtClassName: css.lastTransitionedAtNotEmphasized,
      stateClassName: css.stateNoActionNeeded,
      state: intl.formatMessage({
        id: 'InboxPage.stateExpired',
      }),
    };
  } else if (txIsDeclined(tx)) {
    return {
      nameClassName: css.nameNotEmphasized,
      bookingClassName: css.bookingNoActionNeeded,
      lastTransitionedAtClassName: css.lastTransitionedAtNotEmphasized,
      stateClassName: css.stateNoActionNeeded,
      state: intl.formatMessage({
        id: 'InboxPage.stateDeclined',
      }),
    };
  } else if (txIsAccepted(tx)) {
    return {
      nameClassName: css.nameNotEmphasized,
      bookingClassName: css.bookingNoActionNeeded,
      lastTransitionedAtClassName: css.lastTransitionedAtNotEmphasized,
      stateClassName: css.stateSucces,
      state: intl.formatMessage({
        id: 'InboxPage.stateAccepted',
      }),
    };
  } else if (txIsPaymentConfirmed(tx)) {
    return {
      nameClassName: css.nameNotEmphasized,
      bookingClassName: css.bookingNoActionNeeded,
      lastTransitionedAtClassName: css.lastTransitionedAtNotEmphasized,
      stateClassName: css.stateSucces,
      state: intl.formatMessage({
        id: 'InboxPage.stateAccepted',
      }),
    };
  } else if (txIsCanceled(tx)) {
    return {
      nameClassName: css.nameNotEmphasized,
      bookingClassName: css.bookingNoActionNeeded,
      lastTransitionedAtClassName: css.lastTransitionedAtNotEmphasized,
      stateClassName: css.stateNoActionNeeded,
      state: intl.formatMessage({
        id: 'InboxPage.stateCanceled',
      }),
    };
  } else if (txIsCreatedPayment(tx) || txIsAutoRecreatedPayment(tx)) {
    return {
      nameClassName: css.nameNotEmphasized,
      bookingClassName: css.bookingNoActionNeeded,
      lastTransitionedAtClassName: css.lastTransitionedAtNotEmphasized,
      stateClassName: css.stateNoActionNeeded,
      state: intl.formatMessage({
        id: 'InboxPage.stateCreatedPayment',
      }),
    };
  } else if (txIsDisputed(tx) || exTxIsDisputed(tx)) {
    return {
      nameClassName: css.nameEmphasized,
      bookingClassName: css.bookingActionNeeded,
      lastTransitionedAtClassName: css.lastTransitionedAtEmphasized,
      stateClassName: css.stateActionNeeded,
      state: intl.formatMessage({
        id: 'InboxPage.stateDisputed',
      }),
    };
  } else if (txConfirmedPayment(tx)) {
    return {
      nameClassName: css.nameEmphasized,
      bookingClassName: css.bookingActionNeeded,
      lastTransitionedAtClassName: css.lastTransitionedAtEmphasized,
      stateClassName: css.stateActionNeeded,
      state: intl.formatMessage({
        id: 'InboxPage.stateConfirmedPayment',
      }),
    };
  } else if (txRequestBookingExtend(tx)) {
    return {
      nameClassName: isOrder ? css.nameNotEmphasized : css.nameEmphasized,
      bookingClassName: isOrder ? css.bookingNoActionNeeded : css.bookingActionNeeded,
      lastTransitionedAtClassName: css.lastTransitionedAtNotEmphasized,
      stateClassName: isOrder ? css.stateNoActionNeeded : css.stateActionNeeded,
      state: intl.formatMessage({
        id: 'InboxPage.statePreBookingExtend',
      }),
    };
  } else if (txIsOfferExtendBooking(tx) || exTxIsOfferExtendBooking(tx)) {
    return {
      nameClassName: !isOrder ? css.nameNotEmphasized : css.nameEmphasized,
      bookingClassName: !isOrder ? css.bookingNoActionNeeded : css.bookingActionNeeded,
      lastTransitionedAtClassName: css.lastTransitionedAtNotEmphasized,
      stateClassName: !isOrder ? css.stateNoActionNeeded : css.stateActionNeeded,
      state: intl.formatMessage({
        id: 'InboxPage.stateOfferedExtendBooking',
      }),
    };
  } else if (txIsDeclineExtendBooking(tx)) {
    return {
      nameClassName: isOrder ? css.nameNotEmphasized : css.nameEmphasized,
      bookingClassName: isOrder ? css.bookingNoActionNeeded : css.bookingActionNeeded,
      lastTransitionedAtClassName: css.lastTransitionedAtNotEmphasized,
      stateClassName: isOrder ? css.stateNoActionNeeded : css.stateActionNeeded,
      state: intl.formatMessage({
        id: 'InboxPage.stateDeclineExtendBooking',
      }),
    };
  } else if (txExtendedBooking(tx) || exTxIsExtendedBooking(tx)) {
    return {
      nameClassName: css.nameNotEmphasized,
      bookingClassName: css.bookingNoActionNeeded,
      lastTransitionedAtClassName: css.lastTransitionedAtNotEmphasized,
      stateClassName: css.stateNoActionNeeded,
      state: intl.formatMessage({
        id: 'InboxPage.stateExtendedBooking',
      }),
    };
  } else if (txCompleteBooking(tx) || exTxIsReturnedItem(tx) || exTxIsCompleteBooking(tx)) {
    return {
      nameClassName: css.nameNotEmphasized,
      bookingClassName: css.bookingNoActionNeeded,
      lastTransitionedAtClassName: css.lastTransitionedAtNotEmphasized,
      stateClassName: css.stateNoActionNeeded,
      state: intl.formatMessage({
        id: 'InboxPage.stateCompleteBooking',
      }),
    };
  } else if (txIsDelivered(tx)) {
    return {
      nameClassName: css.nameEmphasized,
      bookingClassName: css.bookingActionNeeded,
      lastTransitionedAtClassName: css.lastTransitionedAtEmphasized,
      stateClassName: css.stateActionNeeded,
      state: intl.formatMessage({
        id: 'InboxPage.stateDelivered',
      }),
    };
  } else if (exTxIsPendingPayment(tx)) {
    const requested = isOrder
      ? {
          nameClassName: css.nameEmphasized,
          bookingClassName: css.bookingActionNeeded,
          lastTransitionedAtClassName: css.lastTransitionedAtEmphasized,
          stateClassName: css.stateActionNeeded,
          state: intl.formatMessage({
            id: 'InboxPage.statePendingPayment',
          }),
        }
      : {
          nameClassName: css.nameNotEmphasized,
          bookingClassName: css.bookingNoActionNeeded,
          lastTransitionedAtClassName: css.lastTransitionedAtNotEmphasized,
          stateClassName: css.stateNoActionNeeded,
          state: intl.formatMessage({
            id: 'InboxPage.statePendingPayment',
          }),
        };
    return requested;
  } else if (exTxIsExpirePayment(tx)) {
    return {
      nameClassName: css.nameNotEmphasized,
      bookingClassName: css.bookingNoActionNeeded,
      lastTransitionedAtClassName: css.lastTransitionedAtNotEmphasized,
      stateClassName: css.stateNoActionNeeded,
      state: intl.formatMessage({
        id: 'InboxPage.stateExtendExpiredPayment',
      }),
    };
  } else if (exTxIsConfirmedPayment(tx)) {
    return {
      nameClassName: css.nameNotEmphasized,
      bookingClassName: css.bookingNoActionNeeded,
      lastTransitionedAtClassName: css.lastTransitionedAtNotEmphasized,
      stateClassName: css.stateSucces,
      state: intl.formatMessage({
        id: 'InboxPage.stateExtendConfirmedPayment',
      }),
    };
  } else if (exTxIsDelivered(tx)) {
    return {
      nameClassName: css.nameEmphasized,
      bookingClassName: css.bookingActionNeeded,
      lastTransitionedAtClassName: css.lastTransitionedAtEmphasized,
      stateClassName: css.stateActionNeeded,
      state: intl.formatMessage({
        id: 'InboxPage.stateExtendDelivered',
      }),
    };
  } else {
    console.warn('This transition is unknown:', tx.attributes.lastTransition);
    return null;
  }
};

// Functional component as internal helper to print BookingTimeInfo if that is needed
const BookingInfoMaybe = props => {
  const { bookingClassName, isOrder, intl, tx, unitType } = props;
  const isEnquiry = txIsEnquired(tx) || txIsOffer(tx);

  if (isEnquiry) {
    return null;
  }

  // If you want to show the booking price after the booking time on InboxPage you can
  // add the price after the BookingTimeInfo component. You can get the price by uncommenting
  // sthe following lines:

  // const bookingPrice = isOrder ? tx.attributes.payinTotal : tx.attributes.payoutTotal;
  // const price = bookingPrice ? formatMoney(intl, bookingPrice) : null;

  // Remember to also add formatMoney function from 'util/currency.js' and add this after BookingTimeInfo:
  // <div className={css.itemPrice}>{price}</div>

  return (
    <div className={classNames(css.bookingInfoWrapper, bookingClassName)}>
      <BookingTimeInfo
        bookingClassName={bookingClassName}
        isOrder={isOrder}
        intl={intl}
        tx={tx}
        unitType={unitType}
        dateType={DATE_TYPE_DATE}
      />
    </div>
  );
};

BookingInfoMaybe.propTypes = {
  intl: intlShape.isRequired,
  isOrder: bool.isRequired,
  tx: propTypes.transaction.isRequired,
  unitType: propTypes.bookingUnitType.isRequired,
};

export const InboxItem = props => {
  const { unitType, type, tx, intl, stateData } = props;
  const { customer, provider } = tx;
  const isOrder = type === 'order';

  const otherUser = isOrder ? provider : customer;
  const otherUserDisplayName = <UserDisplayName user={otherUser} intl={intl} />;
  const isOtherUserBanned = otherUser.attributes.banned;

  const isSaleNotification = !isOrder && txIsRequested(tx);
  const rowNotificationDot = isSaleNotification ? <div className={css.notificationDot} /> : null;
  const lastMessageAtTimestamp = get(tx, 'attributes.metadata.lastMessageAt', 0);
  const lastTransitionedAt = tx.attributes.lastTransitionedAt;
  const lastTransitionedAtTimestamp = Math.floor(new Date(lastTransitionedAt).getTime() / 1000);
  const formattedLastContactAt = lastTransitionedAtTimestamp >= lastMessageAtTimestamp ?
    formatDate(intl, lastTransitionedAt)
    : formatDate(intl, new Date(lastMessageAtTimestamp * 1000));


  const listingName = <span>{tx?.listing?.attributes?.title}</span>;

  const linkClasses = classNames(css.itemLink, {
    [css.bannedUserLink]: isOtherUserBanned,
  });

  return (
    <div className={css.item}>
      <div className={css.itemAvatar}>
        <Avatar user={otherUser} />
      </div>
      <NamedLink
        className={linkClasses}
        name={isOrder ? 'OrderDetailsPage' : 'SaleDetailsPage'}
        params={{ id: tx.id.uuid }}
      >
        <div className={css.rowNotificationDot}>{rowNotificationDot}</div>
        <div className={css.itemInfo}>
          <div className={css.itemListingName}>{listingName}</div>
          <div className={classNames(css.itemUsername, stateData.nameClassName)}>
            {otherUserDisplayName}
          </div>
          <BookingInfoMaybe
            bookingClassName={stateData.bookingClassName}
            intl={intl}
            isOrder={isOrder}
            tx={tx}
            unitType={unitType}
          />
        </div>
        <div className={css.itemState}>
          <div className={classNames(css.stateName, stateData.stateClassName)}>
            {stateData.state}
          </div>
          <div
            className={classNames(css.lastTransitionedAt, stateData.lastTransitionedAtClassName)}
            title={formattedLastContactAt.long}
          >
            {formattedLastContactAt.short}
          </div>
        </div>
      </NamedLink>
    </div>
  );
};

InboxItem.propTypes = {
  unitType: propTypes.bookingUnitType.isRequired,
  type: oneOf(['order', 'sale']).isRequired,
  tx: propTypes.transaction.isRequired,
  intl: intlShape.isRequired,
};

export const InboxPageComponent = props => {
  const {
    unitType,
    currentUser,
    fetchInProgress,
    fetchOrdersOrSalesError,
    intl,
    params,
    providerNotificationCount,
    scrollingDisabled,
    transactions,
    onManageDisableScrolling,
    transactionsFull,
    onFilter,
    onSort,
    onReset,
    totalResultsCount,
  } = props;
  const { tab } = params;
  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const history = useHistory();
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);
  const [initialQueryParams, setInitialQueryParams] = useState(null);
  const [activeQueryParams, setActiveQueryParams] = useState(
    queryString.parse(history.location.search)
  );
  const filteredTotalPages = useMemo(() => Math.ceil(transactions.length / ITEMS_PER_PAGE), [
    transactions.length,
  ]);

  // after transactionsFull is populated, do sort/filter using query params
  useEffect(() => {
    // - totalResultsCount = 0 when there's no (unfiltered) inbox item
    // - transactionsFull.length = 0 when transactionFull has not been populated
    //    from getMarketplaceEntities. It is guaranteed that there're transactions else
    //    the first condition would already be met.
    if (totalResultsCount === 0 || transactionsFull.length !== 0) {
      const { sort, page, tx_state } = queryString.parse(history.location.search);
      const sortParam = VALID_SORT_PARAMS.includes(sort) ? sort : INBOX_SORT_BY_LATEST_CONTACT;
      const txStateParam = VALID_FILTER_PARAMS.includes(tx_state) ? tx_state : '';

      const initialQueryParams = {
        sort: sortParam,
        page,
      };

      if (txStateParam) {
        initialQueryParams.tx_state = txStateParam;
        onFilter({ tx_state: txStateParam });
      }

      onSort(sortParam);
      setInitialQueryParams(initialQueryParams);
    }
  }, [tab, transactionsFull.length]);

  // when the txs is sorted/filtered, update page number if it's not valid
  useEffect(() => {
    if (initialQueryParams) {
      const { page, ...rest } = initialQueryParams;
      const pageParam =
        !page || page < 1 || isNaN(page) ? 1 : Math.min(parseInt(page), filteredTotalPages || 1);
      const activeQueryParams = { ...rest, page: pageParam };
      setActiveQueryParams(activeQueryParams);
      mockRouteUrl({ tab }, { ...activeQueryParams });
    }
  }, [JSON.stringify(initialQueryParams), tab]);

  // change the address bar url when user filter, sort, or go to another page
  useEffect(() => {
    mockRouteUrl({ tab }, { ...activeQueryParams });
  }, [JSON.stringify(activeQueryParams), tab]);

  const validTab = tab === 'orders' || tab === 'sales';
  if (!validTab) {
    return <NotFoundPage />;
  }

  const onOpenMobileModal = () => {
    setIsMobileModalOpen(true);
  };

  const onCloseMobileModal = () => {
    setIsMobileModalOpen(false);
  };

  const onPageChange = ({ selected: selectedPage }) => {
    setActiveQueryParams(prevState => ({ ...prevState, page: selectedPage + 1 }));
  };

  const onFilterInboxItems = filterParam => {
    setActiveQueryParams(prevState => ({ ...prevState, ...filterParam, page: 1 }));
    onFilter(filterParam);
  };

  const onSortInboxItems = sortParam => {
    setActiveQueryParams(prevState => ({ ...prevState, sort: sortParam, page: 1 }));
    onSort(sortParam);
  };

  const onResetFilter = filterParam => {
    setActiveQueryParams({ ...DEFAULT_QUERY_PARAMS });
    onReset(filterParam);
  };

  const isOrders = tab === 'orders';

  const ordersTitle = intl.formatMessage({ id: 'InboxPage.ordersTitle' });
  const salesTitle = intl.formatMessage({ id: 'InboxPage.salesTitle' });
  const title = isOrders ? ordersTitle : salesTitle;

  const currentPageInboxItems = () => {
    const pageFirstIndex = ((activeQueryParams.page || 1) - 1) * ITEMS_PER_PAGE;
    const pageLastIndex = pageFirstIndex + ITEMS_PER_PAGE;
    return transactions.slice(pageFirstIndex, pageLastIndex).map(toTxItem);
  };

  const toTxItem = tx => {
    const type = isOrders ? 'order' : 'sale';
    const stateData = txState(intl, tx, type);

    // Render InboxItem only if the latest transition of the transaction is handled in the `txState` function.
    return stateData ? (
      <li key={tx.id.uuid} className={css.listItem}>
        <InboxItem unitType={unitType} type={type} tx={tx} intl={intl} stateData={stateData} />
      </li>
    ) : null;
  };

  const error = fetchOrdersOrSalesError ? (
    <p className={css.error}>
      <FormattedMessage id="InboxPage.fetchFailed" />
    </p>
  ) : null;

  const noResults =
    !fetchInProgress && totalResultsCount === 0 && !fetchOrdersOrSalesError ? (
      <li key="noResults" className={css.noResults}>
        <FormattedMessage id={isOrders ? 'InboxPage.noOrdersFound' : 'InboxPage.noSalesFound'} />
      </li>
    ) : null;

  const hasOrderOrSaleTransactions = (tx, isOrdersTab, user) => {
    return isOrdersTab
      ? user.id && tx && tx.length > 0 && tx[0].customer.id.uuid === user.id.uuid
      : user.id && tx && tx.length > 0 && tx[0].provider.id.uuid === user.id.uuid;
  };
  const hasTransactions =
    !fetchInProgress && hasOrderOrSaleTransactions(transactions, isOrders, ensuredCurrentUser);

  const providerNotificationBadge =
    providerNotificationCount > 0 ? <NotificationBadge count={providerNotificationCount} /> : null;
  const tabs = [
    {
      text: (
        <span>
          <FormattedMessage id="InboxPage.ordersTabTitle" />
        </span>
      ),
      selected: isOrders,
      linkProps: {
        name: 'InboxPage',
        params: { tab: 'orders' },
      },
    },
    {
      text: (
        <span>
          <FormattedMessage id="InboxPage.salesTabTitle" />
          {providerNotificationBadge}
        </span>
      ),
      selected: !isOrders,
      linkProps: {
        name: 'InboxPage',
        params: { tab: 'sales' },
      },
    },
  ];
  const nav = <TabNav rootClassName={css.tabs} tabRootClassName={css.tab} tabs={tabs} />;

  const topbarClasses = isMobileModalOpen
    ? classNames(css.topbarBehindModal, css.topbar)
    : css.topbar;

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSideNavigation>
        <LayoutWrapperTopbar>
          <TopbarContainer
            className={topbarClasses}
            mobileRootClassName={css.mobileTopbar}
            desktopClassName={css.desktopTopbar}
            currentPage="InboxPage"
          />
        </LayoutWrapperTopbar>
        <LayoutWrapperSideNav className={css.navigation}>
          <h1 className={css.title}>
            <FormattedMessage id="InboxPage.title" />
          </h1>
          {nav}
        </LayoutWrapperSideNav>
        <LayoutWrapperMain>
          <div>
            <InboxFiltersPanel
              key={tab}
              className={css.filterPanel}
              activeQueryParams={activeQueryParams}
              onManageDisableScrolling={onManageDisableScrolling}
              onOpenModal={onOpenMobileModal}
              onCloseModal={onCloseMobileModal}
              showAsModalMaxWidth={MODAL_BREAKPOINT}
              sortItems={onSortInboxItems}
              filterItems={onFilterInboxItems}
              resetFilter={onResetFilter}
              resultsCount={transactions.length}
              totalResultsCount={totalResultsCount}
              inboxItemsAreLoaded={!fetchInProgress}
              tab={tab}
            />
          </div>
          {error}
          <ul className={css.itemList}>
            {!fetchInProgress ? (
              currentPageInboxItems()
            ) : (
              <li className={css.listItemsLoading}>
                <IconSpinner />
              </li>
            )}
            {noResults}
          </ul>
          {hasTransactions ? (
            <Pagination
              pageCount={filteredTotalPages}
              onPageChange={onPageChange}
              forcePage={activeQueryParams.page - 1}
            />
          ) : null}
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSideNavigation>
    </Page>
  );
};

InboxPageComponent.defaultProps = {
  unitType: config.bookingUnitType,
  currentUser: null,
  currentUserHasOrders: null,
  fetchOrdersOrSalesError: null,
  providerNotificationCount: 0,
  sendVerificationEmailError: null,
  totalResultsCount: 0,
};

InboxPageComponent.propTypes = {
  params: shape({
    tab: string.isRequired,
  }).isRequired,

  unitType: propTypes.bookingUnitType,
  currentUser: propTypes.currentUser,
  fetchInProgress: bool.isRequired,
  fetchOrdersOrSalesError: propTypes.error,
  providerNotificationCount: number,
  scrollingDisabled: bool.isRequired,
  transactions: arrayOf(propTypes.transaction).isRequired,
  transactionsFull: arrayOf(propTypes.transaction).isRequired,
  activeQueryParams: object.isRequired,
  totalResultsCount: number,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const {
    fetchInProgress,
    fetchOrdersOrSalesError,
    filteredTransactions,
    activeQueryParams,
    totalResultsCount,
    transactions,
  } = state.InboxPage;

  const { currentUser, currentUserNotificationCount: providerNotificationCount } = state.user;
  return {
    currentUser,
    fetchInProgress,
    fetchOrdersOrSalesError,
    providerNotificationCount,
    scrollingDisabled: isScrollingDisabled(state),
    transactionsFull: transactions,
    transactions: filteredTransactions,
    activeQueryParams,
    totalResultsCount,
  };
};
const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onFilter: filterParam => dispatch(filterTransactions(filterParam)),
  onSort: sortParam => dispatch(sortTransactions(sortParam)),
  onReset: () => dispatch(resetFilter()),
});

const InboxPage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(InboxPageComponent);

InboxPage.loadData = loadData;

export default InboxPage;
